import PropTypes from 'prop-types';
import { normalizeClassList } from '../../utilities/htmlEditorUtils.js';
import { EDITOR_MODES } from '../../utilities/simpleEditorMode.js';

const FRIENDLY_NODE_NAMES = {
  a: 'Link', article: 'Section', blockquote: 'Quote', figcaption: 'Caption', figure: 'Image section',
  footer: 'Footer', h1: 'Main heading', h2: 'Section heading', h3: 'Subheading', h4: 'Heading',
  h5: 'Heading', h6: 'Heading', header: 'Introduction', img: 'Image', li: 'List item', main: 'Main content',
  ol: 'Numbered list', p: 'Paragraph', projects: 'Projects', section: 'Section', skills: 'Skills',
  table: 'Table', ul: 'List', video: 'Video', audio: 'Audio',
};

const nodeLabel = (node, simpleMode = false) => {
  if (simpleMode) return FRIENDLY_NODE_NAMES[node?.type] || 'Content';
  const classes = normalizeClassList(node?.attributes?.class);
  return `${node?.type || 'element'}${classes.map((className) => `.${className}`).join('')}`;
};

const SelectionNavigator = ({
  context,
  onSelect,
  editorMode = EDITOR_MODES.ADVANCED,
  showElementActions = false,
}) => {
  const selectedNode = context.path[context.path.length - 1];
  if (!selectedNode) return null;
  const simpleMode = editorMode === EDITOR_MODES.SIMPLE;

  const navigationActions = [
    ['parent', 'bi-box-arrow-up', 'Select parent', context.parentId],
    ['previous', 'bi-arrow-left', 'Select previous sibling', context.previousSiblingId],
    ['next', 'bi-arrow-right', 'Select next sibling', context.nextSiblingId],
    ['child', 'bi-box-arrow-in-down', 'Select first child', context.firstChildId],
  ];

  return (
    <nav
      className={`selection-navigator ${showElementActions ? 'has-element-actions' : ''}`}
      aria-label="Selected item navigation"
    >
      <div className="selection-breadcrumb" aria-label="Location on page">
        <button
          className="selection-page-root"
          type="button"
          onClick={() => onSelect(null)}
          title="Clear selection"
        >
          <i className="bi bi-file-earmark" aria-hidden="true"></i>
          <span>Page</span>
        </button>
        {context.path.slice(1).map((node, index, nodes) => {
          const isSelected = index === nodes.length - 1;
          return (
            <span className="selection-breadcrumb-step" key={node.id}>
              <i className="bi bi-chevron-right" aria-hidden="true"></i>
              <button
                className={`selection-breadcrumb-button ${isSelected ? 'is-selected' : ''}`}
                type="button"
                onClick={() => onSelect(node.id)}
                aria-current={isSelected ? 'true' : undefined}
                title={`Select ${nodeLabel(node, simpleMode)}`}
              >
                {nodeLabel(node, simpleMode)}
              </button>
            </span>
          );
        })}
      </div>

      <div className="selection-direction-buttons">
        {navigationActions.map(([key, iconClass, title, nodeId]) => (
          <button
            className="btn btn-sm btn-light selection-direction-button"
            type="button"
            onClick={() => nodeId && onSelect(nodeId)}
            disabled={!nodeId}
            title={title}
            aria-label={title}
            key={key}
          >
            <i className={`bi ${iconClass}`} aria-hidden="true"></i>
          </button>
        ))}
      </div>

      {showElementActions ? (
        <div
          className="selection-mobile-actions-slot"
          data-mobile-element-actions-slot
          aria-live="polite"
        />
      ) : null}
    </nav>
  );
};

SelectionNavigator.propTypes = {
  context: PropTypes.shape({
    path: PropTypes.arrayOf(PropTypes.object).isRequired,
    parentId: PropTypes.string,
    previousSiblingId: PropTypes.string,
    nextSiblingId: PropTypes.string,
    firstChildId: PropTypes.string,
  }).isRequired,
  onSelect: PropTypes.func.isRequired,
  editorMode: PropTypes.oneOf(Object.values(EDITOR_MODES)),
  showElementActions: PropTypes.bool,
};

export default SelectionNavigator;
